import React from 'react';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import { useAssessmentStore } from '../../store';

interface DassResultsProps {
  scores: {
    depression: {
      score: number;
      severity: string;
      color: string;
    };
    anxiety: {
      score: number;
      severity: string;
      color: string;
    };
    stress: {
      score: number;
      severity: string;
      color: string;
    };
  };
  onBack: () => void;
}

const DassResults: React.FC<DassResultsProps> = ({ scores, onBack }) => {
  const { setDassResults, setAssessmentComplete } = useAssessmentStore();

  React.useEffect(() => {
    setDassResults(scores);
    setAssessmentComplete('dass');
  }, [scores]);

  const data = [
    { subject: 'Depression', A: scores.depression.score, fullMark: 42 },
    { subject: 'Anxiety', A: scores.anxiety.score, fullMark: 42 },
    { subject: 'Stress', A: scores.stress.score, fullMark: 42 }
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="bg-white rounded-xl shadow-lg p-6 flex-1 overflow-y-auto">
        <div className="flex items-center mb-6">
          <button
            onClick={onBack}
            className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2 className="text-2xl font-bold text-gray-800">Assessment Results</h2>
        </div>

        <div className="space-y-6">
          {/* Radar Chart */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="80%" data={data}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="subject" />
                  <PolarRadiusAxis angle={30} domain={[0, 42]} />
                  <Radar
                    name="Emotional State"
                    dataKey="A"
                    stroke="#3b82f6"
                    fill="#3b82f6"
                    fillOpacity={0.6}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Results Grid */}
          <div className="grid gap-4 md:grid-cols-3">
            {/* Depression */}
            <div className="p-4 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-700 mb-2">Depression Level</h3>
              <div className={`px-3 py-1 rounded-full inline-block ${scores.depression.color}`}>
                {scores.depression.severity}
              </div>
              <p className="mt-2 text-2xl font-bold">{scores.depression.score}</p>
            </div>

            {/* Anxiety */}
            <div className="p-4 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-700 mb-2">Anxiety Level</h3>
              <div className={`px-3 py-1 rounded-full inline-block ${scores.anxiety.color}`}>
                {scores.anxiety.severity}
              </div>
              <p className="mt-2 text-2xl font-bold">{scores.anxiety.score}</p>
            </div>

            {/* Stress */}
            <div className="p-4 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-700 mb-2">Stress Level</h3>
              <div className={`px-3 py-1 rounded-full inline-block ${scores.stress.color}`}>
                {scores.stress.severity}
              </div>
              <p className="mt-2 text-2xl font-bold">{scores.stress.score}</p>
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-blue-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-4">Recommendations</h3>
            <ul className="space-y-3 text-blue-800">
              <li>• Maintain regular sleep and exercise routines</li>
              <li>• Stay connected with friends and family, share your feelings</li>
              <li>• Practice relaxation techniques like deep breathing or meditation</li>
              <li>• Consider seeking professional counseling support if needed</li>
            </ul>
          </div>

          {/* Disclaimer */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-yellow-700">
                <p className="font-medium mb-1">Important Notice</p>
                <p>This assessment tool is for reference only and should not be used for clinical diagnosis. If you are experiencing emotional distress, please seek help from a qualified mental health professional or physician.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DassResults;